import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useId } from 'react';

interface FormInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  type?: string;
  name?: string;
  autoComplete?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /**
   * Overrides the default `${label}${required ? ' *' : ''}` placeholder
   * derivation (e.g. for CMS-configured placeholders). Existing callers that
   * never pass this keep the exact previous behavior.
   */
  placeholder?: string;
  /**
   * Set to `false` when the caller renders the message itself (e.g. in a box
   * above the field). The red border and the aria wiring stay; only the small
   * text below the input is dropped. Mirrors `ZipCity`'s prop of the same name.
   */
  showErrorText?: boolean;
  /**
   * Id of an externally rendered message, used for `aria-describedby` when
   * `showErrorText` is `false`. Without it the input would point at an element
   * that no longer exists.
   */
  errorId?: string;
}

// Exported so other input-like controls that sit next to FormInputs in a
// form (e.g. ZipCity's `formField` variant) can match its sizing exactly
// instead of duplicating the class string.
// `text-base` below `md`, not `text-sm`: Mobile Safari zooms the viewport when a
// focused control's font-size is under 16px, and never zooms back (SH-480). The
// design's 14px returns from `md` up, where no browser does this.
export const formInputBaseClass =
  'block h-[var(--input-height,55px)] w-full rounded-input border bg-[#eae8e7] px-4 py-1 text-base md:text-sm text-foreground outline-none placeholder:text-foreground/60 duration-100 focus:border-[rgba(40,25,10,0.2)] disabled:cursor-not-allowed disabled:opacity-50';

export const FormInput = ({
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  name,
  autoComplete,
  required,
  error,
  disabled,
  placeholder,
  showErrorText = true,
  errorId: externalErrorId,
}: FormInputProps) => {
  const id = useId();
  const errorId = externalErrorId ?? `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type={type}
        name={name}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        // Derived fallback skips the star when the label already ends with
        // one (CMS-managed labels may carry their own " *").
        placeholder={
          placeholder ??
          `${label}${required && !label.trimEnd().endsWith('*') ? ' *' : ''}`
        }
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className={classNames(
          formInputBaseClass,
          error ? 'border-red-400' : 'border-[#eae8e7]',
        )}
      />
      {error && showErrorText && (
        <p id={errorId} className="m-0 mt-1 pl-4 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};
